import React, { useState, useEffect } from "react";
import { Button } from "@heroui/button";
import { Input } from "@heroui/input";
import { Switch } from "@heroui/switch";

import { User, Role } from "../../types/users";

interface UserFormModalProps {
  user: User | null;
  roles: Role[];
  onSave: (userData: any, password?: string) => Promise<void>;
  onCancel: () => void;
  loading: boolean;
}

const UserFormModal: React.FC<UserFormModalProps> = ({
  user,
  roles,
  onSave,
  onCancel,
  loading,
}) => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    phone: "",
    role_id: "",
    is_active: true,
    password: "",
    confirm_password: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form with existing user data if editing
  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username,
        email: user.email || "",
        first_name: user.first_name,
        last_name: user.last_name,
        phone: user.phone || "",
        role_id: user.role_id ? String(user.role_id) : "",
        is_active: user.is_active,
        password: "",
        confirm_password: "",
      });
    }
  }, [user]);

  // Handle form input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };

        delete newErrors[name];

        return newErrors;
      });
    }
  };

  // Handle switch toggle for active status
  const handleActiveChange = (checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      is_active: checked,
    }));
  };

  // Form validation
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
    }

    if (
      formData.email &&
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    ) {
      newErrors.email = "Valid email is required";
    }

    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    }

    // Password validation for new users or when changing password
    if (!user || formData.password) {
      if (!user && !formData.password) {
        newErrors.password = "Password is required for new users";
      } else if (formData.password && formData.password.length < 8) {
        newErrors.password = "Password must be at least 8 characters long";
      }

      if (formData.password !== formData.confirm_password) {
        newErrors.confirm_password = "Passwords do not match";
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Prepare data for submission
    const userData = {
      username: formData.username.trim(),
      email: formData.email.trim() || null,
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      phone: formData.phone.trim() || null,
      role_id: formData.role_id ? parseInt(formData.role_id) : null,
      is_active: formData.is_active,
    };

    // Only include password for new users or when changing password
    const password = formData.password ? formData.password : undefined;

    await onSave(userData, password);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
            {user ? "Edit User" : "Add User"}
          </h3>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input
                  isRequired
                  errorMessage={errors.first_name}
                  isInvalid={!!errors.first_name}
                  label="First Name"
                  name="first_name"
                  placeholder="Enter first name"
                  value={formData.first_name}
                  onChange={handleChange}
                />
              </div>

              <div>
                <Input
                  isRequired
                  errorMessage={errors.last_name}
                  isInvalid={!!errors.last_name}
                  label="Last Name"
                  name="last_name"
                  placeholder="Enter last name"
                  value={formData.last_name}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <Input
                isRequired
                disabled={!!user} // Don't allow changing username for existing users
                errorMessage={errors.username}
                isInvalid={!!errors.username}
                label="Username"
                name="username"
                placeholder="Enter username"
                value={formData.username}
                onChange={handleChange}
              />
            </div>

            <div>
              <Input
                errorMessage={errors.email}
                isInvalid={!!errors.email}
                label="Email"
                name="email"
                placeholder="Enter email address"
                type="email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div>
              <Input
                label="Phone (Optional)"
                name="phone"
                placeholder="Enter phone number"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>

            <div>
              <label
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                htmlFor="role_id"
              >
                Role
              </label>
              <select
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                id="role_id"
                name="role_id"
                value={formData.role_id}
                onChange={handleChange}
              >
                <option value="">Select a role</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                {user ? "Change Password (Optional)" : "Set Password"}
              </h4>

              <div className="space-y-4">
                <div>
                  <Input
                    errorMessage={errors.password}
                    isInvalid={!!errors.password}
                    isRequired={!user}
                    label="Password"
                    name="password"
                    placeholder={user ? "Enter new password" : "Enter password"}
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  {!user && (
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Password must be at least 8 characters long
                    </p>
                  )}
                </div>

                <div>
                  <Input
                    errorMessage={errors.confirm_password}
                    isInvalid={!!errors.confirm_password}
                    isRequired={!user || !!formData.password}
                    label="Confirm Password"
                    name="confirm_password"
                    placeholder="Confirm password"
                    type="password"
                    value={formData.confirm_password}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {formData.is_active ? "User is active" : "User is inactive"}
              </span>
              <Switch
                aria-label="User active status"
                isSelected={formData.is_active}
                size="sm"
                onValueChange={handleActiveChange}
              />
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-2">
            <Button disabled={loading} variant="light" onPress={onCancel}>
              Cancel
            </Button>
            <Button color="primary" isLoading={loading} type="submit">
              {user ? "Update User" : "Create User"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserFormModal;
